import { signIn, signUp, signOut, getCurrentUser, fetchAuthSession, confirmSignUp, resendSignUpCode, updatePassword } from 'aws-amplify/auth';

class CognitoAuthService {
  
  /**
   * Sign in with email and password
   */
  async signIn(email, password) {
    console.log('🔸 Attempting Cognito signIn with email:', email);
    
    // Try different authentication flows in order of preference
    const authFlows = ['USER_SRP_AUTH', 'USER_PASSWORD_AUTH'];
    
    for (const authFlow of authFlows) {
      try {
        console.log(`🔸 Trying auth flow: ${authFlow}`);
        
        const signInOptions = {
          username: email,
          password: password,
        };

        // Only add options if we're specifying a non-default flow
        if (authFlow !== 'USER_SRP_AUTH') {
          signInOptions.options = {
            authFlowType: authFlow
          };
        }

        const { isSignedIn, nextStep } = await signIn(signInOptions);

        console.log(`🔸 ${authFlow} signIn result:`, { isSignedIn, nextStep });

        if (isSignedIn) {
          const user = await this.getCurrentUser();
          return { success: true, user, nextStep };
        }

        return { success: false, nextStep };
        
      } catch (error) {
        console.error(`🔸 ${authFlow} failed:`, error.message);
        
        // If this isn't the last auth flow, continue to next one
        if (authFlow !== authFlows[authFlows.length - 1]) {
          continue;
        }
        
        // This was the last attempt, return the error
        console.error('🔸 All auth flows failed. Final error:', error);
        console.error('🔸 Error details:', {
          name: error.name,
          message: error.message,
          code: error.code || 'No code',
          statusCode: error.statusCode || 'No status code'
        });
        return { success: false, error: error.message };
      }
    }
  }

  /**
   * Sign up with email and password
   */
  async signUp(email, password, username = null) {
    try {
      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: email, // Use email as the primary username
        password: password,
        options: {
          userAttributes: {
            email: email,
            name: email.split('@')[0], // Always use email prefix as name
            preferred_username: email.split('@')[0], // Set preferred_username for consistent extraction
          },
        },
      });

      return { 
        success: true, 
        isSignUpComplete, 
        userId, 
        nextStep,
        message: 'Please check your email for verification code'
      };
    } catch (error) {
      console.error('Cognito sign up error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Confirm sign up with verification code
   */
  async confirmSignUp(email, confirmationCode) {
    try {
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username: email,
        confirmationCode: confirmationCode,
      });

      return { success: true, isSignUpComplete, nextStep };
    } catch (error) {
      console.error('Cognito confirm sign up error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Resend confirmation code
   */
  async resendConfirmationCode(email) {
    try {
      await resendSignUpCode({ username: email });
      return { success: true, message: 'Confirmation code sent' };
    } catch (error) {
      console.error('Cognito resend confirmation error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Change the password for the currently signed-in user.
   * Returns { success, error } — error.name is e.g. 'NotAuthorizedException'
   * (wrong old password) or 'InvalidPasswordException' (policy violation).
   */
  async updatePassword(oldPassword, newPassword) {
    try {
      await updatePassword({ oldPassword, newPassword });
      return { success: true };
    } catch (error) {
      console.error('Cognito update password error:', error);
      return { success: false, error: error.message, code: error.name };
    }
  }

  /**
   * True if the current user signed in via a federated identity provider
   * (Google etc) — those users have no Cognito password to change.
   */
  async isFederatedUser() {
    try {
      const session = await fetchAuthSession();
      const identities = session.tokens?.idToken?.payload?.identities;
      return Array.isArray(identities) && identities.length > 0;
    } catch (error) {
      return false;
    }
  }

  /**
   * Sign out
   */
  async signOut() {
    try {
      await signOut();
      return { success: true };
    } catch (error) {
      console.error('Cognito sign out error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get current user
   */
  async getCurrentUser() {
    try {
      const user = await getCurrentUser();
      return user;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  /**
   * Get access token for API calls
   */
  async getAccessToken() {
    try {
      // Force refresh to ensure we have a valid token
      const session = await fetchAuthSession({ forceRefresh: true });
      const token = session.tokens?.accessToken?.toString();
      return token;
    } catch (error) {
      console.error('Get access token error:', error);
      return null;
    }
  }

  /**
   * Get ID token (contains custom claims from our Lambda trigger)
   */
  async getIdToken() {
    try {
      // Force refresh to ensure we have a valid token
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();
      return idToken;
    } catch (error) {
      console.error('Get ID token error:', error);
      return null;
    }
  }

  /**
   * Get user data from ID token (includes custom claims)
   */
  async getUserData() {
    try {
      // Force refresh to ensure we have a valid token
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken;
      
      if (idToken) {
        // The payload contains our custom claims from the Lambda trigger
        return idToken.payload;
      }
      
      return null;
    } catch (error) {
      console.error('Get user data error:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated() {
    try {
      const user = await getCurrentUser();
      return !!user;
    } catch (error) {
      return false;
    }
  }

  /**
   * Refresh the current session tokens
   */
  async refreshSession() {
    try {
      console.log('🔄 Refreshing Cognito session...');
      const session = await fetchAuthSession({ forceRefresh: true });
      
      if (session.tokens?.accessToken && session.tokens?.idToken) {
        console.log('✅ Session refreshed successfully');
        return {
          success: true,
          accessToken: session.tokens.accessToken.toString(),
          idToken: session.tokens.idToken.toString()
        };
      }
      
      console.error('❌ No valid tokens after refresh');
      return { success: false, error: 'No valid tokens' };
    } catch (error) {
      console.error('❌ Session refresh failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Clear all Amplify/Cognito storage (for debugging)
   */
  clearCognitoStorage() {
    const cognitoKeys = Object.keys(localStorage).filter(key => 
      key.includes('amplify') || 
      key.includes('cognito') || 
      key.includes('CognitoIdentityServiceProvider') ||
      key.includes('aws')
    );
    
    cognitoKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log('🧹 Cleared Cognito storage keys:', cognitoKeys.length);
  }

  /**
   * Social login (Google) - redirects to hosted UI
   */
  signInWithGoogle() {
    // Environment variable access with validation - robust browser access
    const getEnvVar = (key) => {
      try {
        // Try multiple ways to access environment variables in the browser
        let value = null;
        
        // Method 1: Standard process.env (webpack DefinePlugin)
        if (typeof process !== 'undefined' && process.env) {
          value = process.env[key];
        }
        
        // Method 2: Check window.env as fallback
        if (!value && typeof window !== 'undefined' && window.env) {
          value = window.env[key];
        }
        
        if (!value) {
          console.error(`❌ Required environment variable ${key} is not set`);
          console.error(`   Available methods: process.env, window.env`);
          return null;
        }
        return value;
      } catch (e) {
        console.error(`❌ Error accessing environment variable ${key}:`, e);
        return null;
      }
    };

    const cognitoDomain = getEnvVar('REACT_APP_COGNITO_DOMAIN')?.replace('https://cognito-idp.eu-west-1.amazonaws.com/', '');
    const clientId = getEnvVar('REACT_APP_COGNITO_CLIENT_ID');
    
    if (!cognitoDomain || !clientId) {
      console.error('❌ Cannot initiate Google login: Missing Cognito configuration');
      alert('Authentication configuration error. Please contact support.');
      return;
    }
    const redirectUri = process.env.NODE_ENV === 'production'
      ? (getEnvVar('REACT_APP_COGNITO_REDIRECT_SIGNIN') || 'https://app.yatabase.com/auth/callback')
      : (getEnvVar('REACT_APP_COGNITO_REDIRECT_SIGNIN_DEV') || 'https://dev-app.yatabase.com/auth/callback');

    const googleLoginUrl = `https://${cognitoDomain}.auth.eu-west-1.amazoncognito.com/oauth2/authorize?` +
      `identity_provider=Google&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code&` +
      `client_id=${clientId}&` +
      `scope=openid%20email%20profile`;

    window.location.href = googleLoginUrl;
  }
}

// Export singleton instance
const cognitoAuthService = new CognitoAuthService();

// Export refresh token function for axios interceptor
export async function refreshCognitoToken() {
  try {
    console.log('🔄 Attempting to refresh Cognito token via Amplify...');
    const result = await cognitoAuthService.refreshSession();
    
    if (result.success && result.idToken) {
      // Store the new token
      sessionStorage.setItem('complexappToken', result.idToken);
      localStorage.setItem('complexappToken', result.idToken);
      
      console.log('✅ Cognito token refreshed and stored');
      return result.idToken;
    }
    
    console.error('❌ Failed to refresh Cognito token:', result.error);
    return null;
  } catch (error) {
    console.error('❌ Error refreshing Cognito token:', error);
    return null;
  }
}

export default cognitoAuthService; 